import { useState, useCallback } from 'react'
import { Trophy, BarChart3, Play, Pause, Volume2, ChevronDown, ChevronUp, Sparkles, Check, RotateCcw } from 'lucide-react'
import type { EnsembleComparisonData } from '../lib/api'
import { MODEL_INFO } from '../lib/utils'

interface QualityMetrics {
  spectral_clarity: number
  cross_stem_isolation: number
  energy_focus: number
  harmonic_preservation: number
  overall_score: number
  raw_snr: number
}

interface StemComparisonExtended {
  snr: number
  weight: number
  is_winner: boolean
  file_path: string
  quality_metrics?: QualityMetrics
}

export interface StemOverrides {
  [stemName: string]: {
    modelName: string
    filePath: string
  }
}

interface EnsembleComparisonProps {
  comparison: EnsembleComparisonData
  overrides?: StemOverrides
  onOverrideChange?: (overrides: StemOverrides) => void
}

function QualityMetricsBreakdown({ metrics, isWinner }: { metrics: QualityMetrics; isWinner: boolean }) {
  const items = [
    { label: 'Spectral Clarity', value: metrics.spectral_clarity, icon: 'SC' },
    { label: 'Isolation', value: metrics.cross_stem_isolation, icon: 'IS' },
    { label: 'Energy Focus', value: metrics.energy_focus, icon: 'EF' },
    { label: 'Harmonics', value: metrics.harmonic_preservation, icon: 'HP' },
  ]

  return (
    <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-2 text-xs">
          <span className="w-4 text-gray-500 font-mono text-[10px]">{item.icon}</span>
          <div className="flex-1 h-1 bg-surface-600 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${
                isWinner ? 'bg-green-400/60' : 'bg-primary-500/40'
              }`}
              style={{ width: `${Math.min(item.value, 100)}%` }}
            />
          </div>
          <span className={`w-8 text-right ${isWinner ? 'text-green-300' : 'text-gray-400'}`}>
            {item.value.toFixed(0)}%
          </span>
        </div>
      ))}
    </div>
  )
}

export function EnsembleComparison({ comparison, overrides = {}, onOverrideChange }: EnsembleComparisonProps) {
  const [playingAudio, setPlayingAudio] = useState<string | null>(null)
  const [audioElements, setAudioElements] = useState<Record<string, HTMLAudioElement>>({})
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set())

  const stemNames = Object.keys(comparison.stems)
  const models = comparison.models

  const handleSelectModel = useCallback((stemName: string, modelName: string, filePath: string) => {
    if (!onOverrideChange) return

    const newOverrides = { ...overrides }
    // If already selected, remove override (revert to default)
    if (overrides[stemName]?.modelName === modelName) {
      delete newOverrides[stemName]
    } else {
      newOverrides[stemName] = { modelName, filePath }
    }
    onOverrideChange(newOverrides)
  }, [overrides, onOverrideChange])

  const handleResetAll = useCallback(() => {
    if (onOverrideChange) {
      onOverrideChange({})
    }
  }, [onOverrideChange])

  const hasOverrides = Object.keys(overrides).length > 0

  const handlePlay = (stemName: string, modelName: string, filePath: string) => {
    const key = `${stemName}-${modelName}`

    // Stop any currently playing audio
    if (playingAudio && audioElements[playingAudio]) {
      audioElements[playingAudio].pause()
      audioElements[playingAudio].currentTime = 0
    }

    if (playingAudio === key) {
      setPlayingAudio(null)
      return
    }

    // Create or reuse audio element
    let audio = audioElements[key]
    if (!audio) {
      audio = new Audio(filePath)
      audio.onended = () => setPlayingAudio(null)
      setAudioElements(prev => ({ ...prev, [key]: audio }))
    }

    audio.play()
    setPlayingAudio(key)
  }

  const toggleExpanded = (key: string) => {
    setExpandedCells(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const getModelDisplayName = (modelId: string) => {
    return MODEL_INFO[modelId]?.description.split(' ')[0] || modelId
  }

  // Check if any model has quality metrics
  const hasQualityMetrics = stemNames.some(stem =>
    models.some(model => {
      const data = comparison.stems[stem]?.[model] as StemComparisonExtended | undefined
      return data?.quality_metrics !== undefined
    })
  )

  // Find max score for color scaling (use overall_score if available, else SNR)
  const allScores = stemNames.flatMap(stem =>
    models.map(model => {
      const data = comparison.stems[stem]?.[model] as StemComparisonExtended | undefined
      return data?.quality_metrics?.overall_score ?? data?.snr ?? 0
    })
  )
  const maxScore = Math.max(...allScores, 1)

  return (
    <div className="mt-4 p-4 rounded-xl bg-surface-800/50 border border-primary-500/20">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-primary-400" />
        <h4 className="font-semibold text-white">Ensemble Comparison</h4>
        <span className="text-xs text-gray-500 ml-2">
          {comparison.merge_strategy.replace('_', ' ')} merge
        </span>
        {hasQualityMetrics && (
          <span className="flex items-center gap-1 text-xs text-green-400">
            <Sparkles className="w-3 h-3" />
            Quality-weighted
          </span>
        )}
        {hasOverrides && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-amber-400">
              {Object.keys(overrides).length} override{Object.keys(overrides).length !== 1 ? 's' : ''} active
            </span>
            <button
              onClick={handleResetAll}
              className="px-2 py-1 text-xs bg-surface-700 hover:bg-surface-600 text-gray-300 rounded flex items-center gap-1"
              title="Reset all overrides"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          </div>
        )}
        {!hasOverrides && onOverrideChange && (
          <span className="ml-auto text-xs text-gray-500">
            Click "Use" to override a stem
          </span>
        )}
      </div>

      {/* Header row with model names */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-2 px-2 text-gray-400 font-medium">Stem</th>
              {models.map(model => (
                <th key={model} className="text-center py-2 px-3 text-gray-400 font-medium min-w-[140px]">
                  {getModelDisplayName(model)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stemNames.map(stemName => (
              <tr key={stemName} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-3 px-2">
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-4 h-4 text-gray-500" />
                    <span className="text-white capitalize">{stemName}</span>
                  </div>
                </td>
                {models.map(model => {
                  const data = comparison.stems[stemName]?.[model] as StemComparisonExtended | undefined
                  if (!data) {
                    return (
                      <td key={model} className="py-3 px-3 text-center text-gray-600">
                        N/A
                      </td>
                    )
                  }

                  const hasMetrics = data.quality_metrics !== undefined
                  const displayScore = hasMetrics
                    ? data.quality_metrics!.overall_score
                    : data.snr
                  const scorePercent = (displayScore / maxScore) * 100
                  const cellKey = `${stemName}-${model}`
                  const isPlaying = playingAudio === cellKey
                  const isExpanded = expandedCells.has(cellKey)
                  const isSelected = overrides[stemName]?.modelName === model
                  const isOverridden = stemName in overrides && !isSelected

                  return (
                    <td key={model} className="py-3 px-3">
                      <div className={`relative p-2 rounded-lg transition-all ${
                        isSelected
                          ? 'bg-amber-500/20 border-2 border-amber-500/60 ring-2 ring-amber-500/20'
                          : data.is_winner && !isOverridden
                          ? 'bg-green-500/20 border border-green-500/40'
                          : isOverridden
                          ? 'bg-surface-700/30 opacity-60'
                          : 'bg-surface-700/50'
                      }`}>
                        {/* Selection badge */}
                        {isSelected && (
                          <div className="absolute -top-2 -right-2 bg-amber-500 rounded-full p-0.5">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {/* Winner badge (only show if not overridden) */}
                        {data.is_winner && !isSelected && !isOverridden && (
                          <div className="absolute -top-1 -right-1">
                            <Trophy className="w-4 h-4 text-yellow-400" />
                          </div>
                        )}

                        {/* Main score bar */}
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex-1 h-1.5 bg-surface-600 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                data.is_winner ? 'bg-green-400' : 'bg-primary-500/60'
                              }`}
                              style={{ width: `${scorePercent}%` }}
                            />
                          </div>
                        </div>

                        {/* Score value and weight */}
                        <div className="flex items-center justify-between text-xs">
                          {hasMetrics ? (
                            <span className={data.is_winner ? 'text-green-400 font-medium' : 'text-gray-400'}>
                              {displayScore.toFixed(1)}/100
                            </span>
                          ) : (
                            <span className={data.is_winner ? 'text-green-400 font-medium' : 'text-gray-400'}>
                              SNR: {data.snr.toFixed(1)} dB
                            </span>
                          )}
                          <span className={`font-medium ${data.is_winner ? 'text-green-300' : 'text-gray-500'}`}>
                            {(data.weight * 100).toFixed(0)}%
                          </span>
                        </div>

                        {/* Expandable quality metrics */}
                        {hasMetrics && (
                          <>
                            <button
                              onClick={() => toggleExpanded(cellKey)}
                              className="w-full mt-1 py-0.5 text-xs text-gray-500 hover:text-gray-300 flex items-center justify-center gap-1"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronUp className="w-3 h-3" />
                                  Hide metrics
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-3 h-3" />
                                  Show metrics
                                </>
                              )}
                            </button>
                            {isExpanded && (
                              <QualityMetricsBreakdown
                                metrics={data.quality_metrics!}
                                isWinner={data.is_winner}
                              />
                            )}
                          </>
                        )}

                        {/* Action buttons */}
                        <div className="mt-2 flex gap-1">
                          <button
                            onClick={() => handlePlay(stemName, model, data.file_path)}
                            className={`flex-1 py-1 rounded flex items-center justify-center gap-1 text-xs transition-colors ${
                              isPlaying
                                ? 'bg-primary-500 text-white'
                                : 'bg-surface-600 text-gray-300 hover:bg-surface-500'
                            }`}
                          >
                            {isPlaying ? (
                              <Pause className="w-3 h-3" />
                            ) : (
                              <Play className="w-3 h-3" />
                            )}
                          </button>
                          {onOverrideChange && (
                            <button
                              onClick={() => handleSelectModel(stemName, model, data.file_path)}
                              className={`flex-1 py-1 rounded flex items-center justify-center gap-1 text-xs transition-colors ${
                                isSelected
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-surface-600 text-gray-300 hover:bg-amber-500/30 hover:text-amber-300'
                              }`}
                              title={isSelected ? 'Click to reset to default' : 'Use this model for this stem'}
                            >
                              {isSelected ? (
                                <>
                                  <Check className="w-3 h-3" />
                                  Active
                                </>
                              ) : (
                                'Use'
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-white/5 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-500/30 border border-green-500/50" />
          <span>Auto-selected (highest quality)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-amber-500/30 border-2 border-amber-500/60" />
          <span>Your override</span>
        </div>
        <div className="flex items-center gap-1">
          <Trophy className="w-3 h-3 text-yellow-400" />
          <span>Best for this stem</span>
        </div>
        {hasQualityMetrics && (
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-gray-600">Metrics: SC=Clarity, IS=Isolation, EF=Energy, HP=Harmonics</span>
          </div>
        )}
      </div>
    </div>
  )
}
